import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Leaf, Brain, Users, ShoppingBag, Calendar, TrendingUp, CheckCircle } from 'lucide-react';
import Footer from '@/components/Footer';

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const { user, signup, login } = useAuth();
  const navigate = useNavigate();

  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  React.useEffect(() => {
    if (user) {
      if (user.role === 'admin') navigate('/admin/dashboard');
      else if (user.role === 'doctor') navigate('/doctor/dashboard');
      else if (user.role === 'corporate') navigate('/corporate/dashboard');
      else if (user.role === 'wellness_center') navigate('/center/dashboard');
      else navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSignup = async (e) => {
    e.preventDefault();
    const result = await signup(signupData.email, signupData.password, signupData.name, signupData.role);
    if (result.success) {
      toast.success('Welcome to चिकित्साHub!');
      setShowAuth(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login(loginData.email, loginData.password);
    if (result.success) {
      toast.success('Welcome back!');
      setShowAuth(false);
    } else {
      toast.error(result.error);
    }
  };

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay" data-testid="hero-section">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1591647273840-8826c6cfab8b)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
          }}
        />
        
        <div className="container mx-auto px-6 lg:px-24 py-20 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <div className="text-sm uppercase tracking-wide text-secondary font-medium mb-6">
                Ancient Wisdom • Modern Science
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>
                Your Personal
                <br />
                <span className="text-primary">Ayurveda</span> Wellness
                <br />
                Companion
              </h1>
              
              <p className="text-lg md:text-xl leading-relaxed text-gray-600 mb-10 max-w-2xl">
                AI-powered holistic wellness platform combining 5,000 years of Ayurvedic wisdom with modern intelligence for personalized care, products, and treatments.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => setShowAuth(true)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="get-started-btn"
                >
                  Get Started Free
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setShowAuth(true)}
                  className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-8 py-6 text-lg transition-all duration-300"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>

              <div className="mt-12 flex items-center space-x-8">
                <div>
                  <div className="text-3xl font-bold text-primary">500+</div>
                  <div className="text-sm text-gray-600">Verified Doctors</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">10k+</div>
                  <div className="text-sm text-gray-600">Happy Users</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">99%</div>
                  <div className="text-sm text-gray-600">Satisfaction</div>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1765813136594-c504b124210b"
                  alt="Wellness meditation"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-muted/30" data-testid="features-section">
        <div className="container mx-auto px-6 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Complete Wellness Ecosystem
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need for your holistic health journey in one intelligent platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Brain className="h-12 w-12 text-primary" />,
                title: 'AI Dosha Analysis',
                description: 'Discover your unique Prakriti and Vikriti with AI-powered assessment',
              },
              {
                icon: <Users className="h-12 w-12 text-primary" />,
                title: 'Expert Consultations',
                description: 'Connect with verified Ayurvedic doctors via video or chat',
              },
              {
                icon: <ShoppingBag className="h-12 w-12 text-primary" />,
                title: 'Authentic Products',
                description: 'Curated Ayurvedic medicines and wellness products',
              },
              {
                icon: <Calendar className="h-12 w-12 text-primary" />,
                title: 'Wellness Planning',
                description: 'Daily Dinacharya and lifestyle recommendations',
              },
              {
                icon: <TrendingUp className="h-12 w-12 text-primary" />,
                title: 'Progress Tracking',
                description: 'Monitor your health journey with detailed analytics',
              },
              {
                icon: <Leaf className="h-12 w-12 text-primary" />,
                title: 'Holistic Care',
                description: 'Treatments, yoga, diet, and lifestyle guidance',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white/80 backdrop-blur-md border border-border/40 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 card-lift"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-medium mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32" data-testid="how-it-works-section">
        <div className="container mx-auto px-6 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Your Wellness Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Simple steps to transformative health
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Sign Up', description: 'Create your account in seconds' },
              { step: '02', title: 'Dosha Assessment', description: 'Complete AI-powered health quiz' },
              { step: '03', title: 'Get Your Plan', description: 'Receive personalized wellness roadmap' },
              { step: '04', title: 'Start Healing', description: 'Book doctors, order products, track progress' },
            ].map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="text-6xl font-bold text-accent/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-accent/30" style={{ transform: 'translateX(-50%)' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="container mx-auto px-6 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Begin Your Holistic Wellness Journey Today
          </h2>
          <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto">
            Join thousands discovering authentic Ayurvedic care with modern convenience
          </p>
          <Button
            size="lg"
            onClick={() => setShowAuth(true)}
            className="bg-white text-primary hover:bg-white/90 rounded-full px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            data-testid="cta-signup-btn"
          >
            Start Free Assessment
          </Button>
        </div>
      </section>

      <Footer />

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
              Welcome to चिकित्साHub
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="signup" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
            </TabsList>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
                <div>
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    data-testid="signup-name-input"
                    value={signupData.name}
                    onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    data-testid="signup-email-input"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    data-testid="signup-password-input"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="signup-role">I am a</Label>
                  <select
                    id="signup-role"
                    data-testid="signup-role-select"
                    className="w-full border border-gray-200 rounded-lg h-10 px-3"
                    value={signupData.role}
                    onChange={(e) => setSignupData({ ...signupData, role: e.target.value })}
                  >
                    <option value="user">User / Patient</option>
                    <option value="doctor">Ayurvedic Doctor</option>
                    <option value="wellness_center">Wellness Center</option>
                  </select>
                </div>
                <Button type="submit" className="w-full" data-testid="signup-submit-btn">
                  Sign Up
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    data-testid="login-email-input"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    data-testid="login-password-input"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="login-submit-btn">
                  Login
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
